/**
 * The decisions the account screens make that do not need React: where a
 * sign-in lands, what the navigation is carrying, and how a stored instant is
 * written for a reader. They live here so they can be tested as plain
 * functions, and so the screens stay thin (architectural constraint: logic
 * lives in pure functions).
 *
 * @author John Grimes
 */

/** Where a visitor lands when nothing else says otherwise. */
export const DEFAULT_LANDING_PATH = "/views";

/** How a date is written where the user reads it. */
const dateFormat = new Intl.DateTimeFormat("en-AU", { dateStyle: "medium" });

/**
 * Decides where a sign-in lands. Only a path inside the app is honoured, so a
 * crafted link cannot use the sign-in screen to bounce someone off site.
 *
 * @param next - The return path the route guard remembered, if any.
 * @returns The path to navigate to.
 *
 * @example
 * ```ts
 * landingPath("/views/abc"); // "/views/abc"
 * landingPath("https://evil.example/x"); // "/views"
 * ```
 */
export const landingPath = (next: string | undefined): string =>
  next !== undefined && next.startsWith("/") && !next.startsWith("//")
    ? next
    : DEFAULT_LANDING_PATH;

/**
 * Reads the one-time recovery codes out of the navigation state, tolerating a
 * visit that carries none: an address typed in, a reload, or a step back all
 * arrive that way, and none of them may be shown codes (FR-002).
 *
 * @param state - The navigation state, whatever it turns out to be.
 * @returns The codes, or an empty list when there are none to show.
 *
 * @example
 * ```ts
 * readRecoveryCodes({ recoveryCodes: ["aaaaa-bbbbb"] }); // ["aaaaa-bbbbb"]
 * readRecoveryCodes(null); // []
 * ```
 */
export const readRecoveryCodes = (state: unknown): string[] => {
  if (typeof state !== "object" || state === null) return [];
  if (!("recoveryCodes" in state)) return [];
  const codes = state.recoveryCodes;
  if (!Array.isArray(codes)) return [];
  return codes.filter((code): code is string => typeof code === "string");
};

/**
 * Writes a timestamp the way the user reads it, tolerating one this browser
 * cannot parse rather than showing them the word "Invalid".
 *
 * @param timestamp - The ISO 8601 instant the backend stored.
 * @returns The date in words, or the raw value when it cannot be read.
 *
 * @example
 * ```ts
 * formatDate("2026-08-01T00:00:00.000Z"); // "1 Aug 2026"
 * ```
 */
export const formatDate = (timestamp: string): string => {
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? timestamp : dateFormat.format(parsed);
};
